// Calendar linkage for unified auth. Sign-in requests calendar scope, and the
// resulting Google tokens are POSTed to the API to become the calendar connection
// (see lib/supabase.js signInWithGoogle + pages/AuthCallback.jsx).
import { apiFetch, API_URL } from '../lib/api';
import { supabase } from '../lib/supabase';

// True if the user has at least one HEALTHY calendar connection. A degraded/failed
// connection (e.g. a scope-short token) is treated as not-connected so the caller
// triggers a repair (a fresh consent) rather than trusting a broken calendar.
export async function hasCalendarConnected() {
  try {
    const res = await apiFetch('/v1/calendars/connections');
    const list = res?.connections || res?.data || res || [];
    if (!Array.isArray(list)) return Boolean(list);
    return list.some((c) => c && c.sync_status !== 'degraded' && c.sync_status !== 'failed');
  } catch (err) {
    if (err.status === 404) return false;
    throw err;
  }
}

// Unified auth: store the Google tokens captured from the sign-in session as the
// calendar connection. The tokens come from the SAME Google grant the user signed
// in with, so the connected calendar is guaranteed to be the signed-in account.
export async function linkGoogleCalendar({ accessToken, refreshToken }) {
  return apiFetch('/v1/calendars/google/link', {
    method: 'POST',
    body: { access_token: accessToken, refresh_token: refreshToken || null },
  });
}

// Microsoft equivalent of linkGoogleCalendar — store the Graph tokens captured
// from the unified Microsoft sign-in session as the calendar connection.
export async function linkMicrosoftCalendar({ accessToken, refreshToken }) {
  return apiFetch('/v1/calendars/microsoft/link', {
    method: 'POST',
    body: { access_token: accessToken, refresh_token: refreshToken || null },
  });
}

// Connect an Apple (iCloud) calendar. Apple has no OAuth — the user supplies an
// Apple ID + app-specific password, which we POST directly. Deliberately NOT routed
// through apiFetch: the endpoint returns 401 for *bad Apple credentials*, which is
// not a dead Vennio session — apiFetch's error handling shouldn't treat it as one.
// Returns { connected, provider, replaced, calendars }. Throws an Error with a
// `.status` (401 = bad creds, 422 = no calendars) so the UI can map specifics.
export async function connectApple({ appleId, appPassword }) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw Object.assign(new Error('Not signed in'), { status: 401 });

  const res = await fetch(`${API_URL}/v1/calendars/apple/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ apple_id: appleId, app_password: appPassword }),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.detail || body.title || body.message || 'Failed to connect Apple Calendar');
    err.status = res.status;
    throw err;
  }
  return body;
}

// Force an immediate calendar sync. The server-side callback auto-selects the
// primary calendar but doesn't sync, so without this the first overlap would treat
// not-yet-synced time as free until the 15-min cron. Fire-and-continue.
export async function syncNow() {
  return apiFetch('/v1/calendars/sync', { method: 'POST' });
}
